<template>
  <div class="ebay_option_modal_overlay" v-if="current">
    <div class="ebay_option_modal">
      <div class="modal_header">
        <h3>이베이 옵션 수동 입력</h3>
        <span class="queue_count" v-if="queueCount > 1">대기 {{ queueCount - 1 }}건 더 있음</span>
        <button class="close_btn" @click="skip" title="건너뛰기">×</button>
      </div>

      <div class="modal_body">
        <div class="product_info">
          <p class="product_title">{{ current.title }}</p>
          <p class="product_meta">상품코드 {{ current.code }} · 계정 {{ current.marketAccount }}</p>
          <p class="fail_reason">
            <strong>실패 사유</strong>
            {{ current.failReason }}
          </p>
        </div>

        <div class="guide_box">
          <button class="guide_toggle" @click="showGuide = !showGuide">
            입력 방법 {{ showGuide ? '접기 ▲' : '보기 ▼' }}
          </button>
          <ol v-if="showGuide">
            <li>
              이베이는 옵션명을 <b>카테고리에서 정해 둔 이름(변형 속성)</b>으로만 받습니다.
              옵션 그룹마다 <b>이베이 옵션명</b> 목록에서 알맞은 것을 고르세요.
              <span class="example">예) 색상 → Color, 사이즈 → Size, 용량 → Volume</span>
            </li>
            <li>
              옵션값은 <b>영어로</b> 입력하세요. 한글은 이베이에서 거부됩니다.
              <span class="example">예) 블랙 → Black, 프리 → One Size, 250mm → 250 mm</span>
            </li>
            <li>
              <span class="badge strict">표준값만</span> 표시가 있는 옵션명은 이베이가 정한 값만 받습니다.
              입력칸을 누르면 나오는 목록에서 가장 가까운 값을 고르세요. 목록에 없는 값은 등록되지 않습니다.
              <span class="example">예) FREE → One Size, S(52-54) → S</span>
            </li>
            <li>
              <span class="badge free">직접입력</span> 표시가 있는 옵션명은 영어로 자유롭게 입력해도 됩니다. 최대 65자입니다.
            </li>
            <li>
              같은 옵션 그룹 안에서 서로 다른 옵션이 <b>같은 값이 되면 안 됩니다.</b>
              두 그룹을 같은 이베이 옵션명으로 고를 수도 없습니다.
            </li>
            <li>
              저장한 내용은 <b>이 상품의 이베이 등록에 계속 적용</b>되고, 입력한 값은 번역하지 않고 그대로 보냅니다.
              자동 번역값이 맞으면 그대로 두면 됩니다.
            </li>
          </ol>
        </div>

        <p class="no_aspect" v-if="aspects.length === 0">
          이 카테고리의 이베이 옵션명 목록을 불러오지 못했습니다. 이베이 옵션명을 영어로 직접 입력해주세요.
        </p>

        <div class="option_group" v-for="(group, gIdx) in form" :key="group.groupName">
          <div class="group_header">
            <div class="group_name">
              <label>옵션 그룹</label>
              <span>{{ group.groupName }}</span>
              <small v-if="group.translatedName && group.translatedName !== group.groupName">(자동번역: {{ group.translatedName }})</small>
            </div>
            <div class="aspect_select">
              <label>이베이 옵션명</label>
              <select v-if="aspects.length > 0" v-model="group.aspectName">
                <option value="">선택해주세요</option>
                <option v-for="aspect in aspects" :key="aspect.name" :value="aspect.name">
                  {{ aspect.name }}{{ aspect.mode === 'SELECTION_ONLY' ? ' (표준값만)' : '' }}
                </option>
              </select>
              <input v-else type="text" v-model.trim="group.aspectName" placeholder="예) Color" maxlength="40" />
              <span v-if="aspectOf(group)" class="badge" :class="isStrict(group) ? 'strict' : 'free'">
                {{ isStrict(group) ? '표준값만' : '직접입력' }}
              </span>
            </div>
          </div>
          <p class="error_text" v-if="groupErrors[gIdx]">{{ groupErrors[gIdx] }}</p>

          <table class="value_table">
            <thead>
              <tr>
                <th>원본 옵션값</th>
                <th>자동번역</th>
                <th>이베이에 보낼 값</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, vIdx) in group.values" :key="value.optionName">
                <td>{{ value.optionName }}</td>
                <td class="translated">{{ value.translatedName || '-' }}</td>
                <td>
                  <input
                    type="text"
                    v-model.trim="value.value"
                    :list="isStrict(group) ? datalistId(gIdx) : null"
                    :class="{ invalid: valueErrors[gIdx] && valueErrors[gIdx][vIdx] }"
                    maxlength="65"
                    placeholder="영문 값 입력"
                  />
                  <p class="error_text" v-if="valueErrors[gIdx] && valueErrors[gIdx][vIdx]">{{ valueErrors[gIdx][vIdx] }}</p>
                </td>
              </tr>
            </tbody>
          </table>
          <datalist v-if="isStrict(group)" :id="datalistId(gIdx)">
            <option v-for="v in aspectOf(group).values" :key="v" :value="v" />
          </datalist>
          <p class="allowed_values" v-if="isStrict(group)">
            사용 가능한 값: {{ aspectOf(group).values.slice(0, 30).join(', ') }}{{ aspectOf(group).values.length > 30 ? ` 외 ${aspectOf(group).values.length - 30}개` : '' }}
          </p>
        </div>
      </div>

      <div class="modal_footer">
        <button class="cancel_btn" @click="skip">닫기</button>
        <button class="save_btn" @click="save(false)" :disabled="processing">저장만</button>
        <button class="confirm_btn" @click="save(true)" :disabled="processing">
          {{ processing ? '처리중...' : '저장 후 다시 등록' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { marketApi } from '@/api/market';

const HANGUL = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;

export default {
  name: 'EbayOptionErrorModal',
  data() {
    return {
      form: [],
      showGuide: true,
      processing: false,
      submitted: false
    };
  },
  computed: {
    current() {
      return this.socketStore.ebayOptionErrors[0] || null;
    },
    queueCount() {
      return this.socketStore.ebayOptionErrors.length;
    },
    aspects() {
      return this.current?.aspects || [];
    },
    groupErrors() {
      if (!this.submitted) return [];
      return this.form.map((group, gIdx) => {
        if (!group.aspectName) return '이베이 옵션명을 선택해주세요.';
        if (HANGUL.test(group.aspectName)) return '이베이 옵션명은 영어로 입력해주세요.';
        if (this.aspects.length > 0 && !this.aspectOf(group)) return '목록에 있는 이베이 옵션명만 쓸 수 있습니다.';
        const dupIdx = this.form.findIndex(
          (other, oIdx) => oIdx !== gIdx && other.aspectName && other.aspectName.toLowerCase() === group.aspectName.toLowerCase()
        );
        if (dupIdx !== -1) return `'${this.form[dupIdx].groupName}' 그룹과 이베이 옵션명이 같습니다. 서로 다른 옵션명을 골라주세요.`;
        return '';
      });
    },
    valueErrors() {
      if (!this.submitted) return [];
      return this.form.map((group) => {
        const aspect = this.aspectOf(group);
        const strict = this.isStrict(group);
        const seen = {};
        return group.values.map((value) => {
          if (!value.value) return '값을 입력해주세요.';
          if (HANGUL.test(value.value)) return '영어로 입력해주세요.';
          if (strict && !aspect.values.some((v) => v.toLowerCase() === value.value.toLowerCase())) {
            return '표준값 목록에 있는 값만 쓸 수 있습니다.';
          }
          const key = value.value.toLowerCase();
          if (seen[key]) return `'${seen[key]}' 옵션과 값이 같습니다.`;
          seen[key] = value.optionName;
          return '';
        });
      });
    },
    hasError() {
      return this.groupErrors.some((x) => x) || this.valueErrors.some((list) => list.some((x) => x));
    }
  },
  watch: {
    current: {
      immediate: true,
      handler(item) {
        this.submitted = false;
        this.form = (item?.optionGroups || []).map((group) => ({
          groupName: group.groupName,
          translatedName: group.translatedName,
          aspectName: group.aspectName || '',
          values: (group.values || []).map((value) => ({
            optionName: value.optionName,
            translatedName: value.translatedName,
            value: value.value || ''
          }))
        }));
      }
    }
  },
  methods: {
    aspectOf(group) {
      if (!group.aspectName) return null;
      return this.aspects.find((x) => x.name.toLowerCase() === group.aspectName.toLowerCase()) || null;
    },
    isStrict(group) {
      const aspect = this.aspectOf(group);
      return !!aspect && aspect.mode === 'SELECTION_ONLY' && aspect.values?.length > 0;
    },
    datalistId(gIdx) {
      return `ebay_option_values_${gIdx}`;
    },
    skip() {
      this.socketStore.shiftEbayOptionError();
    },
    // 표준값은 대소문자까지 이베이 목록과 같아야 하므로 목록 표기로 맞춘다
    buildEbayEtc() {
      return {
        optionGroups: this.form.map((group) => {
          const aspect = this.aspectOf(group);
          return {
            groupName: group.groupName,
            aspectName: aspect ? aspect.name : group.aspectName,
            values: group.values.map((value) => {
              const standard = this.isStrict(group)
                ? aspect.values.find((v) => v.toLowerCase() === value.value.toLowerCase())
                : null;
              return { optionName: value.optionName, value: standard || value.value };
            })
          };
        })
      };
    },
    async save(retry) {
      this.submitted = true;
      if (this.hasError) {
        this.common.setToast('purple', 'info', '빨간색으로 표시된 항목을 확인해주세요.');
        return;
      }

      const item = this.current;
      this.processing = true;
      try {
        const saveRes = await marketApi.SaveEbayEtc(item.productId, this.buildEbayEtc());
        if (saveRes.status !== 'Success') {
          this.common.setDialog({ subTitle: saveRes.message ?? '저장 중 오류가 발생했습니다.' });
          return;
        }

        if (!retry) {
          this.common.setToast('green', 'smile', '저장했습니다. 다음 이베이 등록부터 적용됩니다.');
          this.skip();
          return;
        }

        const uploadRes = await marketApi.DoProductUpload(this.socketStore.connectionId, {
          objectIds: [item.productId],
          uploadTargets: [{ market: item.market, marketAccount: item.marketAccount, marketIdx: item.marketIdx }],
          skipTranslation: item.skipTranslation
        });
        if (uploadRes.status === 'Success') {
          this.common.setToast('green', 'smile', '저장 후 이베이 등록을 다시 시작했습니다.');
          this.skip();
        } else {
          // 같은 탭에서 업로드가 진행 중이면 재등록이 막힌다. 저장은 됐으니 나중에 다시 등록하면 된다.
          this.common.setDialog({
            subTitle: `옵션은 저장했지만 다시 등록하지 못했습니다.\n${uploadRes.message ?? ''}\n진행 중인 업로드가 끝난 뒤 이 상품을 다시 등록해주세요.`
          });
          this.skip();
        }
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ebay_option_modal_overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.ebay_option_modal {
  width: 760px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: $white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.modal_header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid $table_line;

  h3 {
    font-size: 18px;
    font-weight: 700;
  }

  .queue_count {
    font-size: 13px;
    color: $category;
  }

  .close_btn {
    margin-left: auto;
    font-size: 24px;
    line-height: 1;
    background: none;
    border: none;
    cursor: pointer;
  }
}

.modal_body {
  padding: 16px 20px;
  overflow-y: auto;
}

.product_info {
  margin-bottom: 12px;

  .product_title {
    font-weight: 700;
  }

  .product_meta {
    font-size: 13px;
    color: $category;
    margin-top: 2px;
  }

  .fail_reason {
    margin-top: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: $red;
    background: #fff4f4;
    border-radius: 4px;
    word-break: break-all;

    strong {
      margin-right: 6px;
    }
  }
}

.guide_box {
  margin-bottom: 16px;
  padding: 10px 14px;
  background: $background_tooltip;
  border: 1px solid #f3e2b6;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.6;

  .guide_toggle {
    font-weight: 700;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  ol {
    margin-top: 6px;
    padding-left: 18px;
    list-style: decimal;
  }

  li + li {
    margin-top: 4px;
  }

  .example {
    display: block;
    color: $category;
  }
}

.no_aspect {
  margin-bottom: 12px;
  font-size: 13px;
  color: $warning;
}

.badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 11px;
  border-radius: 3px;
  white-space: nowrap;

  &.strict {
    color: $white;
    background: $warning;
  }

  &.free {
    color: $white;
    background: $green;
  }
}

.option_group {
  margin-bottom: 18px;
  padding: 12px;
  border: 1px solid $table_line;
  border-radius: 6px;

  .group_header {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
  }

  label {
    display: block;
    font-size: 12px;
    color: $category;
    margin-bottom: 2px;
  }

  .group_name span {
    font-weight: 700;
  }

  .group_name small {
    margin-left: 4px;
    color: $category;
  }

  .aspect_select {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;

    label {
      width: 100%;
    }

    select,
    input {
      min-width: 200px;
      height: 32px;
      padding: 0 8px;
      border: 1px solid $line;
      border-radius: 4px;
    }
  }
}

.value_table {
  width: 100%;
  margin-top: 10px;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid $table_line;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: $table_box;
    font-weight: 600;
  }

  .translated {
    color: $category;
  }

  input {
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: 1px solid $line;
    border-radius: 4px;

    &.invalid {
      border-color: $red;
    }
  }
}

.allowed_values {
  margin-top: 6px;
  font-size: 12px;
  color: $category;
  word-break: break-all;
}

.error_text {
  margin-top: 4px;
  font-size: 12px;
  color: $red;
}

.modal_footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid $table_line;

  button {
    height: 36px;
    padding: 0 16px;
    border-radius: 4px;
    border: 1px solid $line;
    background: $white;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .confirm_btn {
    color: $button_text;
    background: $button_main_color;
    border-color: $button_main_color;
  }
}
</style>
